import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Pencil, Search, UserPlus, Power, X, XCircle, RotateCcw } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import AssignEmployeeDrawer from "./AssignEmployeeDrawer";
import { warehouseService } from "../../../services/warehouse.service";
import { getWarehouseRuntimeState } from "../../../utils/warehouseRuntime";

function ChipButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-[38px] rounded-full bg-[#d86a78] px-6 text-[13px] font-semibold text-white hover:bg-[#c95b69] active:scale-[0.99] transition"
    >
      {children}
    </button>
  );
}

function SelectLike({ value, onChange, placeholder, options }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const normalizedOptions = [
    { value: "", label: placeholder },
    ...options.map((option) => ({ value: option, label: option })),
  ];

  const selected =
    normalizedOptions.find((option) => String(option.value) === String(value)) ||
    normalizedOptions[0];

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={wrapRef} className="relative h-[38px]">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[34px] w-full items-center justify-between rounded-[8px] border bg-white px-4 text-left text-[11px] outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f9dfe4]"
            : "border-[#f2c6cc] hover:border-[#d86a78]"
        }`}
      >
        <span className="truncate text-slate-500">{selected?.label || placeholder}</span>
        <ChevronDown
          size={15}
          className={`shrink-0 text-slate-400 transition ${
            open ? "rotate-180 text-[#d86a78]" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[40px] z-[120] max-h-[240px] overflow-y-auto rounded-[12px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {normalizedOptions.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={`${placeholder}-${option.value || "all"}`}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-[9px] px-3 py-2 text-left text-[11px] transition ${
                  active
                    ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                    : "text-slate-600 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {/* {active ? <span className="ml-3 h-2 w-2 rounded-full bg-[#d86a78]" /> : null} */}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function StatusPill({ value, runtime }) {
  const state = runtime || getWarehouseRuntimeState({ status: value });
  const toneClass =
    state.operationalStatus === "open"
      ? "bg-emerald-50 text-emerald-600"
      : state.operationalStatus === "closed_by_time"
        ? "bg-amber-50 text-amber-700"
        : "bg-rose-50 text-rose-600";

  return (
    <span
      className={
        "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold " +
        toneClass
      }
      title={state.reason || ""}
    >
      {state.label}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-[12px] border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold text-slate-400">{label}</div>
      <div className="mt-0.5 text-[13px] font-semibold text-slate-700">
        {value || "-"}
      </div>
    </div>
  );
}

function WarehouseDetailsModal({ open, id, onClose }) {
  const [state, setState] = useState({ loading: false, error: "", data: null });

  useEffect(() => {
    if (!open || !id) return;

    let alive = true;
    (async () => {
      try {
        setState({ loading: true, error: "", data: null });
        const res = await warehouseService.getWarehouse(id);
        const w =
          res?.data?.data ||
          res?.data?.warehouse ||
          res?.warehouse ||
          res?.data ||
          res;

        if (!alive) return;
        setState({ loading: false, error: "", data: w });
      } catch (e) {
        if (!alive) return;
        setState({
          loading: false,
          error: e?.message || "Failed to load warehouse details",
          data: null,
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, id]);

  if (!open) return null;

  const w = state.data;
  const runtime = getWarehouseRuntimeState(w || {});

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-[900px] rounded-[16px] bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="text-[16px] font-bold text-[#d86a78]">
            Warehouse Details
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {state.loading ? (
            <div className="text-[13px] text-slate-500">Loading...</div>
          ) : state.error ? (
            <div className="text-[13px] font-semibold text-rose-600">
              {state.error}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Code" value={w?.code} />
              <Field label="Name" value={w?.name} />
              <Field label="Manual Status" value={w?.status} />
              <Field label="Current Operational Status" value={runtime.label} />
              <Field label="Permanently Closed At" value={w?.permanently_closed_at} />
              <Field label="Permanent Close Reason" value={w?.permanent_close_reason} />

              <Field label="Warehouse Email" value={w?.email} />
              <Field label="Warehouse Phone" value={w?.phone} />

              <Field label="Manager Name" value={w?.manager_name} />
              <Field label="Manager Email" value={w?.manager_email} />
              <Field label="Manager Emp ID" value={w?.manager_emp_id} />

              <Field label="Address Line 1" value={w?.address_line1} />
              <Field label="Address Line 2" value={w?.address_line2} />
              <Field label="Pincode" value={w?.pincode} />
              <Field label="City" value={w?.city} />
              <Field label="State" value={w?.state} />
              <Field label="Country" value={w?.country} />

              <Field label="Latitude" value={w?.lat} />
              <Field label="Longitude" value={w?.lng} />

              <Field label="Opening Time" value={w?.opening_time} />
              <Field label="Closing Time" value={w?.closing_time} />
              <Field label="Timezone" value={w?.timezone} />

              <Field label="Order Radius (KM)" value={w?.order_radius_km} />
              <Field label="Max Active Orders" value={w?.max_active_orders} />
              <Field label="Avg Delivery Time (mins)" value={w?.avg_delivery_time_mins} />
              <Field label="Buffer Time (mins)" value={w?.buffer_time_mins} />

              <Field label="Notes" value={w?.notes} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WarehouseDetails() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const getPositiveNumber = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [onboarding, setOnboarding] = useState(
    () => searchParams.get("onboarding") || ""
  );
  const [location, setLocation] = useState(() => searchParams.get("location") || "");
  const [page, setPage] = useState(() =>
    getPositiveNumber(searchParams.get("page"), 1)
  );
  const [pageSize, setPageSize] = useState(() =>
    getPositiveNumber(searchParams.get("limit"), 10)
  );

  const [assign, setAssign] = useState({ open: false, row: null });

  const [statusPopup, setStatusPopup] = useState({
    open: false,
    row: null,
    nextStatus: "",
  });

  const [permanentClosePopup, setPermanentClosePopup] = useState({
    open: false,
    row: null,
  });

  const [reopenPopup, setReopenPopup] = useState({
    open: false,
    row: null,
  });

  const [reloadKey, setReloadKey] = useState(0);
  const [minuteTick, setMinuteTick] = useState(0);

  // NEW: details modal
  const [details, setDetails] = useState({ open: false, id: null });

  const [apiState, setApiState] = useState({
    loading: false,
    error: "",
    items: [],
    total: 0,
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setMinuteTick((tick) => tick + 1);
    }, 60 * 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 1) params.set("page", String(page));
    if (pageSize !== 10) params.set("limit", String(pageSize));
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (onboarding) params.set("onboarding", onboarding);
    if (location) params.set("location", location);

    setSearchParams(params, { replace: true });
  }, [page, pageSize, q, status, onboarding, location, setSearchParams]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setApiState((s) => ({ ...s, loading: true, error: "" }));
        const res = await warehouseService.listWarehouses({
          page,
          limit: pageSize,
          q: q.trim(),
          status: status ? String(status).toLowerCase() : "",
        });

        const items = Array.isArray(res?.items) ? res.items : [];
        const total = Number(res?.total || items.length || 0);

        if (!alive) return;
        setApiState({ loading: false, error: "", items, total });
      } catch (e) {
        if (!alive) return;
        setApiState((s) => ({
          ...s,
          loading: false,
          error: e?.message || "Failed to load warehouses",
          items: [],
          total: 0,
        }));
      }
    })();

    return () => {
      alive = false;
    };
  }, [page, pageSize, q, status, reloadKey]);

  const mapped = useMemo(() => {
    return (apiState.items || []).map((w) => ({
      id: w.id,
      status: w.status || "-",
      runtime: getWarehouseRuntimeState(w),
      name: w.name || "-",
      email: w.email || "-",
      phone: w.phone || "-",
      manager: w.manager_name || "-",
      onboarding: "-",
      location: w.city || w.state || "-",
      _raw: w,
    }));
  }, [apiState.items, minuteTick]);

  const options = useMemo(() => {
    const uniq = (arr) =>
      Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b));
    return {
      status: uniq(mapped.map((w) => w.status)),
      onboarding: [],
      location: uniq(mapped.map((w) => w.location)),
    };
  }, [mapped]);

  const rows = useMemo(() => {
    const total = apiState.total || 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, pageCount);
    const items = mapped.slice(0, pageSize);

    return { total, pageCount, currentPage, items };
  }, [mapped, apiState.total, page, pageSize]);

  useEffect(() => {
    if (!apiState.loading && page > rows.pageCount) setPage(rows.pageCount);
  }, [apiState.loading, page, rows.pageCount]);

  useEffect(() => {
    if (apiState.loading || rows.total <= 0) return;

    const nextPageSize =
      rows.total < 10
        ? rows.total
        : pageSize > rows.total
          ? rows.total
          : pageSize < 10
            ? 10
            : pageSize;

    if (nextPageSize !== pageSize) {
      setPage(1);
      setPageSize(nextPageSize);
    }
  }, [apiState.loading, rows.total, pageSize]);

  const pageSizeOptions = useMemo(() => {
    if (rows.total <= 0) return [10];

    const base = [10, 25, 50, 100, 250, 500];
    const opts = base.filter((n) => n <= rows.total);

    if (!opts.includes(rows.total)) opts.push(rows.total);

    return Array.from(new Set(opts)).sort((a, b) => a - b);
  }, [rows.total]);

  const onChangePageSize = (value) => {
    const n = Number(value);
    setPage(1);
    setPageSize(Number.isFinite(n) && n > 0 ? n : 10);
  };

  const canPrev = rows.currentPage > 1;
  const canNext = rows.currentPage < rows.pageCount;
  const startIndex = rows.total === 0 ? 0 : (rows.currentPage - 1) * pageSize + 1;
  const endIndex =
    rows.total === 0 ? 0 : Math.min(rows.currentPage * pageSize, rows.total);

  async function confirmToggleStatus() {
    const row = statusPopup.row;
    const nextStatus = statusPopup.nextStatus;
    if (!row?.id || !nextStatus) return;

    try {
      await warehouseService.setWarehouseStatus(row.id, nextStatus);
      setStatusPopup({ open: false, row: null, nextStatus: "" });
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(e?.message || "Failed to update warehouse status");
    }
  }

  async function confirmPermanentClose() {
    const row = permanentClosePopup.row;
    if (!row?.id) return;

    try {
      await warehouseService.permanentCloseWarehouse(row.id, {
        reason: "Closed by admin",
      });
      setPermanentClosePopup({ open: false, row: null });
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(e?.message || "Failed to permanently close warehouse");
    }
  }

  async function confirmReopenWarehouse() {
    const row = reopenPopup.row;
    if (!row?.id) return;

    try {
      await warehouseService.reopenWarehouse(row.id, { status: "active" });
      setReopenPopup({ open: false, row: null });
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(e?.message || "Failed to reopen warehouse");
    }
  }

  return (
    <div className="w-full">
      <AssignEmployeeDrawer
        open={assign.open}
        warehouse={assign.row}
        onClose={() => setAssign({ open: false, row: null })}
      />

      <WarehouseDetailsModal
        open={details.open}
        id={details.id}
        onClose={() => setDetails({ open: false, id: null })}
      />

      <ConfirmModal
        open={statusPopup.open}
        icon="warning"
        variant="confirm"
        title={`Are you sure you want to ${statusPopup.nextStatus === "active" ? "Activate" : "Deactivate"
          } ${statusPopup.row?.name || ""}?`}
        description="This will change the warehouse status."
        onClose={() => setStatusPopup({ open: false, row: null, nextStatus: "" })}
        onConfirm={confirmToggleStatus}
        cancelText="NO"
        confirmText="YES"
      />

      <ConfirmModal
        open={permanentClosePopup.open}
        icon="x"
        variant="confirm"
        title={`Permanently close ${permanentClosePopup.row?.name || "this warehouse"}?`}
        description="This is not the same as inactive. User-side serviceability, home catalog, category browser, search, product detail, and cart context will stop selecting this warehouse. Normal activate/deactivate will stay blocked; use Reopen only when you intentionally want to bring it back."
        onClose={() => setPermanentClosePopup({ open: false, row: null })}
        onConfirm={confirmPermanentClose}
        cancelText="NO"
        confirmText="CLOSE"
      />

      <ConfirmModal
        open={reopenPopup.open}
        icon="warning"
        variant="confirm"
        title={`Reopen ${reopenPopup.row?.name || "this warehouse"}?`}
        description="This will change the warehouse back to active, clear the permanent-close fields, clear user-side warehouse caches, and make it eligible again for nearby-user warehouse selection."
        onClose={() => setReopenPopup({ open: false, row: null })}
        onConfirm={confirmReopenWarehouse}
        cancelText="NO"
        confirmText="REOPEN"
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
          WAREHOUSE DETAILS
        </h1>

        <div className="flex flex-wrap items-center gap-3">
          <ChipButton onClick={() => navigate("/admin/dashboard/warehouse/new")}>
            Add New Warehouse
          </ChipButton>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-[250px]">
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder=""
              className="h-[34px] w-full rounded-[8px] border border-[#f2c6cc] bg-white pl-9 pr-4 text-[11px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
            />
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
            />
          </div>

          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3 sm:w-[520px]">
            <SelectLike
              value={status}
              onChange={(v) => {
                setPage(1);
                setStatus(v);
              }}
              placeholder="Status"
              options={options.status}
            />
            <SelectLike
              value={onboarding}
              onChange={(v) => {
                setPage(1);
                setOnboarding(v);
              }}
              placeholder="Onboarding"
              options={options.onboarding}
            />
            <SelectLike
              value={location}
              onChange={(v) => {
                setPage(1);
                setLocation(v);
              }}
              placeholder="Location"
              options={options.location}
            />
          </div>
        </div>

        <div className="lg:ml-auto">
          <div className="inline-flex h-[30px] items-center rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[14px] font-semibold text-[#d86a78]">
            {rows.total} Total Warehouses
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-[14px] border border-slate-200/70 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="w-[40px] border-b border-slate-200/70 px-4 py-3" />
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  Status
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  W-Name
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  W-Email
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  W-Phone
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  W-Manager
                </th>
                {/* <th className="border-b border-slate-200/70 px-4 py-3 text-center text-[15px] font-bold text-[#d86a78]">
                  Onboarding
                </th> */}
                <th className="border-b border-slate-200/70 px-4 py-3 text-center text-[15px] font-bold text-[#d86a78]">
                  Location
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-center text-[15px] font-bold text-[#d86a78]">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.items.map((w) => {
                const normalizedStatus = String(w.status).toLowerCase();
                const isActive = normalizedStatus === "active";
                const isPermanentlyClosed = normalizedStatus === "permanently_closed";
                const nextStatus = isActive ? "inactive" : "active";

                return (
                  <tr key={w.id} className="hover:bg-slate-50/50">
                    <td className="border-b border-slate-200/70 px-4 py-4">
                      {/* <input
                        type="checkbox"
                        className="h-[14px] w-[14px] rounded border-slate-300 text-[#d86a78]"
                      /> */}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4">
                      <StatusPill value={w.status} runtime={w.runtime} />
                    </td>

                    {/* CLICK to view details */}
                    <td
                      className="border-b border-slate-200/70 px-4 py-4 text-[15px] text-slate-500 cursor-pointer hover:text-[#d86a78]"
                      onClick={() => setDetails({ open: true, id: w.id })}
                      title="View details"
                    >
                      {w.name}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[15px] text-slate-500">
                      {w.email}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[15px] text-slate-500">
                      {w.phone}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[15px] text-slate-500">
                      {w.manager}
                    </td>

                    {/* <td className="border-b border-slate-200/70 px-4 py-4 text-center text-[15px] text-slate-400">
                      {w.onboarding}
                    </td> */}

                    <td className="border-b border-slate-200/70 px-4 py-4 text-center text-[15px] text-slate-400">
                      {w.location}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4">
                      <div className="flex items-center justify-center gap-3 text-slate-700">
                        {/* EDIT: open same page but in edit mode */}
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const res = await warehouseService.getWarehouse(w.id);
                              const warehouse =
                                res?.warehouse || res?.data?.warehouse || res?.data || res;

                              navigate("/admin/dashboard/warehouse/new", {
                                state: { mode: "edit", warehouseId: w.id, warehouse },
                              });
                            } catch (e) {
                              alert(e?.message || "Failed to load warehouse");
                            }
                          }}
                          className="rounded-lg p-2 hover:bg-slate-100"
                          aria-label="Edit"
                        >
                          <Pencil size={18} strokeWidth={2.6} />
                        </button>

                        {/* <button
                          type="button"
                          onClick={() => setAssign({ open: true, row: w })}
                          className="rounded-lg p-2 hover:bg-slate-100"
                          aria-label="Assign"
                        >
                          <UserPlus size={18} strokeWidth={2.6} />
                        </button> */}

                        {isPermanentlyClosed ? (
                          <button
                            type="button"
                            onClick={() =>
                              setReopenPopup({
                                open: true,
                                row: w,
                              })
                            }
                            className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50"
                            aria-label="Reopen warehouse"
                            title="Reopen Warehouse"
                          >
                            <RotateCcw size={18} strokeWidth={2.6} />
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                setStatusPopup({
                                  open: true,
                                  row: w,
                                  nextStatus,
                                })
                              }
                              className={
                                "rounded-lg p-2 hover:bg-slate-100 " +
                                (isActive ? "text-emerald-600" : "text-rose-600")
                              }
                              aria-label={isActive ? "Deactivate" : "Activate"}
                              title={isActive ? "Deactivate" : "Activate"}
                            >
                              <Power size={18} strokeWidth={2.6} />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                setPermanentClosePopup({
                                  open: true,
                                  row: w,
                                })
                              }
                              className="rounded-lg p-2 text-rose-700 hover:bg-rose-50"
                              aria-label="Permanent close"
                              title="Permanent Close"
                            >
                              <XCircle size={18} strokeWidth={2.6} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>

      <div className="mt-5 flex flex-col gap-3 rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-4 text-[12px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-center sm:text-left">
          Showing {startIndex}-{endIndex} of {rows.total}
        </div>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center justify-between gap-3 sm:justify-start">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onChangePageSize(e.target.value)}
              className="h-9 min-w-[88px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78]"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-2 sm:justify-start">
            <button
              type="button"
              onClick={() => canPrev && setPage((p) => p - 1)}
              disabled={!canPrev}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Prev
            </button>

            <span className="min-w-[86px] text-center">
              Page {rows.currentPage} / {rows.pageCount}
            </span>

            <button
              type="button"
              onClick={() => canNext && setPage((p) => p + 1)}
              disabled={!canNext}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>  
        </div>
      </div>
    </div>
  );
}